/**
 * Contract Read Operations Hook
 *
 * For reading data from contracts (read-only calls, no gas required)
 * Uses public RPC endpoint
 */

import { useState, useCallback } from 'react';
import { createPublicClient, http } from 'viem';
import { base } from 'viem/chains';
import { CONTRACTS } from '@/config/contracts';
import { ALCHEMY_API_KEY } from '@/utils/constants';
import DasherABI from '@/abi/Dasher';

// Create public client for read operations
const publicClient = createPublicClient({
  chain: base,
  transport: http(`https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`),
});

interface ChallengeData {
  stakeAmount: bigint;
  startDate: bigint;
  endDate: bigint;
  tokenAddress: string;
}

export function useContractReads() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Check if a user has joined a specific challenge
   */
  const hasUserJoined = useCallback(
    async (challengeAddress: string, userAddress: string): Promise<boolean> => {
      try {
        setIsLoading(true);
        setError(null);

        const result = await publicClient.readContract({
          address: challengeAddress as `0x${string}`,
          abi: DasherABI,
          functionName: 'hasUserJoined',
          args: [userAddress as `0x${string}`],
        });

        return result as boolean;
      } catch (err: any) {
        console.error('[ContractRead] hasUserJoined failed:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Get challenge details
   */
  const getChallengeData = useCallback(
    async (challengeAddress: string): Promise<ChallengeData> => {
      try {
        setIsLoading(true);
        setError(null);

        // Read multiple values in parallel
        const [stakeAmount, startDate, endDate, tokenAddress] = await Promise.all([
          publicClient.readContract({
            address: challengeAddress as `0x${string}`,
            abi: DasherABI,
            functionName: 'challengeStakeAmount',
          }),
          publicClient.readContract({
            address: challengeAddress as `0x${string}`,
            abi: DasherABI,
            functionName: 'startDate',
          }),
          publicClient.readContract({
            address: challengeAddress as `0x${string}`,
            abi: DasherABI,
            functionName: 'endDate',
          }),
          publicClient.readContract({
            address: challengeAddress as `0x${string}`,
            abi: DasherABI,
            functionName: 'tokenAddress',
          }),
        ]);

        return {
          stakeAmount: stakeAmount as bigint,
          startDate: startDate as bigint,
          endDate: endDate as bigint,
          tokenAddress: tokenAddress as string,
        };
      } catch (err: any) {
        console.error('[ContractRead] getChallengeData failed:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  /**
   * Get the minimum challenge duration from factory
   */
  const getMinChallengeDuration = useCallback(async (): Promise<number> => {
    try {
      setIsLoading(true);
      setError(null);

      const result = await publicClient.readContract({
        address: CONTRACTS.DASHER_FACTORY.address,
        abi: CONTRACTS.DASHER_FACTORY.abi,
        functionName: 'MIN_CHALLENGE_DURATION',
      });

      return Number(result);
    } catch (err: any) {
      console.error('[ContractRead] getMinChallengeDuration failed:', err);
      setError(err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Get the grace period for withdrawals
   */
  const getGracePeriod = useCallback(
    async (challengeAddress: string): Promise<number> => {
      try {
        setIsLoading(true);
        setError(null);

        const result = await publicClient.readContract({
          address: challengeAddress as `0x${string}`,
          abi: DasherABI,
          functionName: 'GRACE_PERIOD',
        });

        return Number(result);
      } catch (err: any) {
        console.error('[ContractRead] getGracePeriod failed:', err);
        setError(err);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    // Read functions
    hasUserJoined,
    getChallengeData,
    getMinChallengeDuration,
    getGracePeriod,

    // State
    isLoading,
    error,
  };
}
